import { Receipt as InvoiceIcon } from "@mui/icons-material";
import { Chip, Grid, Stack, Typography } from "@mui/material";
import { uniq } from "lodash";
import isNil from "lodash/isNil";
import type { FC, PropsWithChildren } from "react";
import { AssociatedFilesView } from "../components/associatedFiles/AssociatedFilesView";
import EntityCard from "../components/entityCard/EntityCard";
import { formatDate, getOrderDisplayMode } from "../data/dataUtils";
import { DisplayModeAccent } from "../orders/orderCard/orderCardUtils";
import MagicoInvoiceChip from "../sharedComponents/invoices/MagicoInvoiceChip";
import { useDragAndDrop } from "../sharedComponents/useDragAndDrop";
import CostStatusView from "./CostStatusView";
import type { CostInvoiceStatus, InvoiceMagicoTwin } from "./costApi";
import type { Cost } from "./costTypes";
import { calculateMargin } from "./costsUtils";

interface CostInvoiceProps {
  readonly invoiceNumber: string;
  readonly url?: string;
  readonly status: CostInvoiceStatus;
}

interface CostInvoicesProps {
  readonly invoices: (CostInvoiceProps & { readonly magico: InvoiceMagicoTwin | null })[];
}

function invoiceStatusToColor(status: CostInvoiceStatus) {
  switch (status) {
    case "FOR_PAYMENT":
      return "#000000";
    case "ADVANCE_PAYMENT":
      return "#ffae00";
    case "PAID":
      return "#01951d";
    case "EXCESS_PAYMENT":
      return "#c60d00";
    default:
      return "#000000";
  }
}

function costTitle({ order }: Pick<Cost, "order">) {
  const uniqueProductsCount = uniq(order.products.map((p) => p.id)).length;
  return uniqueProductsCount === 1 ? `${order.id.userFriendlyId}: ${order.products[0].name}` : order.id.userFriendlyId;
}

const CostInvoice: FC<CostInvoiceProps> = ({ invoiceNumber, url, status }) => {
  const color = invoiceStatusToColor(status) as never;
  return (
    <Chip
      label={invoiceNumber}
      variant="outlined"
      color={color}
      component="a"
      href={url}
      target="_blank"
      onClick={(e) => e.stopPropagation()}
      data-testid="invoice-file-chip"
      icon={<InvoiceIcon />}
      sx={color === "primary" ? undefined : { color, borderColor: color }}
      clickable
    />
  );
};

interface CostCardTextProps {
  readonly title: string;
}

const CostCardText: FC<PropsWithChildren<CostCardTextProps>> = ({ children, title }) => (
  <Typography
    title={title}
    noWrap
    sx={{
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
  >
    {children}
  </Typography>
);

const CostInvoices: FC<CostInvoicesProps> = ({ invoices }) => (
  <Stack direction="column" spacing={1}>
    {invoices.map((i) => (
      <Stack
        key={i.invoiceNumber}
        direction="row"
        spacing={1}
        role="group"
        aria-label={`Faktura ${i.invoiceNumber}`}
        sx={{ flexWrap: "wrap", alignItems: "center" }}
      >
        {i.url ? (
          <CostInvoice invoiceNumber={i.invoiceNumber} url={i.url} status={i.status} />
        ) : (
          <CostCardText title="Numer faktury">{i.invoiceNumber}</CostCardText>
        )}
        {i.magico && <MagicoInvoiceChip twin={i.magico} />}
      </Stack>
    ))}
  </Stack>
);

export type CostCardCost = Pick<
  Cost,
  | "id"
  | "date"
  | "order"
  | "totalAmount"
  | "currency"
  | "bookingNumber"
  | "status"
  | "provider"
  | "customer"
  | "files"
  | "invoices"
>;

interface Props {
  value: CostCardCost;
  onClick: () => void;
  onContextMenu: () => void;
  onPdfDropped: (arg: { file: File; cost: CostCardCost }) => void;
}

export const CostCard: FC<Props> = ({ value, onClick, onContextMenu, onPdfDropped }) => {
  const margin = calculateMargin(value.order.price, value.order.currency, value.currency, value.invoices);
  const { isDragging, ...dndHandlers } = useDragAndDrop((dataTransfer) => {
    const files = Array.from(dataTransfer.files);
    const pdf = files.find((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (pdf) {
      onPdfDropped({ file: pdf, cost: value });
    }
  });

  return (
    <EntityCard
      role="ADMIN"
      accent={DisplayModeAccent[getOrderDisplayMode(value.order)]}
      backgroundColor={isDragging ? "lightblue" : undefined}
      onClick={onClick}
      onContextMenu={onContextMenu}
      alignChildren="center"
      childrenSpacing={1}
      {...dndHandlers}
    >
      <Grid size={1}>
        <CostCardText title="Numer rezerwacji">{value.bookingNumber}</CostCardText>
      </Grid>
      <Grid size={1} onClick={(e) => e.stopPropagation()}>
        <CostStatusView value={value.status} />
      </Grid>
      <Grid size={1}>
        <CostCardText title="Dostawca">{value.provider?.name}</CostCardText>
      </Grid>
      <Grid size={1}>
        <CostCardText title="Data wypłynięcia">{formatDate(value.date)}</CostCardText>
      </Grid>
      <Grid size={2}>
        <CostCardText title="Zamówienie">{costTitle(value)}</CostCardText>
      </Grid>
      <Grid size={2}>
        <CostCardText title="Klient">{value.customer?.name}</CostCardText>
      </Grid>
      <Grid size={1}>
        <CostCardText title="Do zapłaty armatorowi">
          {value.totalAmount} {value.currency}
        </CostCardText>
      </Grid>
      <Grid size={1}>
        <CostCardText title="Marża">{isNil(margin) ? "" : `${margin}%`}</CostCardText>
      </Grid>
      <Grid size={1}>
        <CostInvoices invoices={value.invoices} />
      </Grid>
      <Grid size={1}>
        <AssociatedFilesView values={value.files} />
      </Grid>
    </EntityCard>
  );
};
